/**
 * Les langues de la démo — ce que les fans étrangers écrivent, ce que ça veut
 * dire en français, et ce que deviennent les phrases du poste chez eux.
 *
 * Kayla, 07-10-2026 : « c'est bien qu'il y ait des trucs dans plusieurs
 * langues, mais quand on va dans la discussion, il n'y a pas la traduction
 * automatique ». Au poste, chaque entrant d'un fan étranger arrive avec sa
 * traduction (`ApiMessage.traductions`, posée par Cyrano à la persistance), et
 * ce que le chatteur tape en français part traduit. La démo n'a pas de
 * traducteur : elle porte donc les deux sens ÉCRITS ICI, une phrase à la fois.
 *
 * ⛔ Le sens « envoyer » ne connaît QUE les phrases de ce fichier — celles que
 * Francis propose (`conseil.ts`) et celles des scènes. Une phrase libre ne se
 * traduit pas en démo, et la démo le DIT (cf. `serveur.ts`) plutôt que de
 * renvoyer le français tel quel en le faisant passer pour une traduction.
 */

export type Langue = "fr" | "en" | "de" | "it" | "es" | "pt";

/** Une phrase écrite dans la langue du fan, et ce qu'elle dit en français. */
export interface Dit {
  texte: string;
  fr: string;
}

/**
 * Ce que le chatteur écrit en français → ce que le fan lit.
 *
 * La clé est la phrase française EXACTE (normalisée par `cle`) : c'est celle
 * que Francis insère dans le composeur. Une phrase absente d'une langue n'est
 * pas traduite dans cette langue.
 */
const DICTIONNAIRE: Record<string, Partial<Record<Langue, string>>> = {
  "hey toi 😊 bienvenue, t'as fait vite": {
    en: "hey you 😊 welcome, that was quick",
    de: "hey du 😊 willkommen, das ging schnell",
    it: "ehi tu 😊 benvenuto, sei stato veloce",
    es: "hola tú 😊 bienvenido, qué rápido",
    pt: "olá tu 😊 bem-vindo, foste rápido",
  },
  "j'ai une vidéo de 6 min où je me touche en pensant à un mec comme toi… rien que pour toi c'est 50 😏": {
    en: "i have a 6 min video where i touch myself thinking about a guy like you… just for you it's 50 😏",
    de: "ich habe ein 6-minuten-video, in dem ich mich anfasse und an einen typen wie dich denke… nur für dich 50 😏",
    it: "ho un video di 6 minuti dove mi tocco pensando a un ragazzo come te… solo per te sono 50 😏",
    es: "tengo un vídeo de 6 min donde me toco pensando en un chico como tú… solo para ti son 50 😏",
    pt: "tenho um vídeo de 6 min em que me toco a pensar num rapaz como tu… só para ti são 50 😏",
  },
  "je te l'envoie, tu vas voir tout ce que t'as raté 😘": {
    en: "i'm sending it, you'll see everything you missed 😘",
    de: "ich schicke es dir, du wirst sehen, was du verpasst hast 😘",
    it: "te lo mando, vedrai tutto quello che ti sei perso 😘",
    es: "te lo mando, vas a ver todo lo que te perdiste 😘",
    pt: "vou mandar, vais ver tudo o que perdeste 😘",
  },
  "c'est 50 bb, et crois moi tu vas pas le regretter 😏": {
    en: "it's 50 babe, and trust me you won't regret it 😏",
    de: "es kostet 50 schatz, und glaub mir, du wirst es nicht bereuen 😏",
    it: "sono 50 amore, e fidati non te ne pentirai 😏",
    es: "son 50 cariño, y créeme no te vas a arrepentir 😏",
    pt: "são 50 amor, e acredita que não te vais arrepender 😏",
  },
  "si t'as pas tout, je peux te la faire à 40, mais pas moins": {
    en: "if you don't have it all, i can do 40, but not less",
    de: "wenn du nicht alles hast, mache ich sie dir für 40, aber nicht weniger",
    it: "se non hai tutto, te lo faccio a 40, ma non meno",
    es: "si no tienes todo, te la dejo en 40, pero no menos",
    pt: "se não tens tudo, faço-te a 40, mas não menos",
  },
  "en vrai c'est pas possible, mais je peux te montrer ce que je ferais si t'étais là 😏": {
    en: "honestly that's not possible, but i can show you what i'd do if you were here 😏",
    de: "ehrlich gesagt geht das nicht, aber ich kann dir zeigen, was ich tun würde, wenn du hier wärst 😏",
    it: "sinceramente non è possibile, ma posso mostrarti cosa farei se fossi qui 😏",
    es: "la verdad no es posible, pero te puedo enseñar lo que haría si estuvieras aquí 😏",
    pt: "sinceramente não é possível, mas posso mostrar-te o que faria se estivesses aqui 😏",
  },
  "merci bb 😘 regarde la jusqu'au bout et dis moi ce que t'en penses": {
    en: "thank you babe 😘 watch it till the end and tell me what you think",
    de: "danke schatz 😘 schau es bis zum ende und sag mir, was du denkst",
    it: "grazie amore 😘 guardalo fino alla fine e dimmi cosa ne pensi",
    es: "gracias cariño 😘 mírala hasta el final y dime qué te parece",
    pt: "obrigada amor 😘 vê até ao fim e diz-me o que achas",
  },
  "j'ai encore plus fou que ça, tu veux que je te montre ? 😏": {
    en: "i have something even crazier, want me to show you? 😏",
    de: "ich habe noch was verrückteres, soll ich es dir zeigen? 😏",
    it: "ho qualcosa di ancora più folle, vuoi che te lo mostri? 😏",
    es: "tengo algo todavía más loco, ¿quieres que te lo enseñe? 😏",
    pt: "tenho uma coisa ainda mais louca, queres que te mostre? 😏",
  },
  "t'es passé où ? j'ai pensé à toi aujourd'hui 😘": {
    en: "where did you go? i thought about you today 😘",
    de: "wo bist du hin? ich habe heute an dich gedacht 😘",
    it: "dove sei finito? oggi ho pensato a te 😘",
    es: "¿dónde te metiste? hoy pensé en ti 😘",
    pt: "onde andas? pensei em ti hoje 😘",
  },
  "t'as vu le lien ? je l'ai fait rien que pour toi 😘": {
    en: "did you see the link? i made it just for you 😘",
    de: "hast du den link gesehen? ich habe es nur für dich gemacht 😘",
    it: "hai visto il link? l'ho fatto solo per te 😘",
    es: "¿viste el enlace? lo hice solo para ti 😘",
    pt: "viste o link? fiz só para ti 😘",
  },
  "dis moi ce que t'aimerais voir, je vais te chauffer comme il faut 😏": {
    en: "tell me what you'd like to see, i'm gonna get you going 😏",
    de: "sag mir, was du sehen willst, ich mache dich richtig heiß 😏",
    it: "dimmi cosa ti piacerebbe vedere, ti scaldo come si deve 😏",
    es: "dime qué te gustaría ver, te voy a calentar como se debe 😏",
    pt: "diz-me o que gostavas de ver, vou aquecer-te como deve ser 😏",
  },
};

function cle(texte: string): string {
  return texte.trim().toLocaleLowerCase().replace(/\s+/g, " ");
}

const PAR_CLE = new Map(
  Object.entries(DICTIONNAIRE).map(([fr, traductions]) => [cle(fr), traductions]),
);

/** La phrase française du chatteur, dans la langue du fan — ou `null` quand
 *  la démo ne la connaît pas. */
export function traduireVers(texte: string, langue: Langue): string | null {
  if (langue === "fr") return texte;
  return PAR_CLE.get(cle(texte))?.[langue] ?? null;
}

/** La langue de la carte (`"EN"`) en code de traduction (`"en"`). */
export function langueDe(codeDeCarte: string | null | undefined): Langue {
  const l = (codeDeCarte ?? "fr").toLowerCase();
  return (["fr", "en", "de", "it", "es", "pt"] as const).find((x) => x === l) ?? "fr";
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Ce que les fans écrivent, par langue                                       */
/* ────────────────────────────────────────────────────────────────────────── */

export interface LangueDesFans {
  langue: Langue;
  prenoms: string[];
  /** Le premier message d'un fan qui arrive — souvent deux bulles. */
  arrivees: Dit[][];
  /** Ce qu'il écrit pour revenir après un silence. */
  retours: Dit[];
  /** Ses réponses au fil de la conversation. */
  repliques: Dit[];
  /** Ce qu'il demande quand il veut acheter. */
  prix: Dit[];
}

const fr = (texte: string): Dit => ({ texte, fr: texte });

export const FANS: Record<Langue, LangueDesFans> = {
  fr: {
    langue: "fr",
    prenoms: [
      "Antoine", "Bilal", "Cédric", "Damien", "Enzo", "Fabien", "Gaël", "Hugo",
      "Jordan", "Karim", "Loïc", "Maxime", "Quentin", "Tristan", "Yannick", "Romain",
    ],
    arrivees: [
      [fr("salut"), fr("je viens de ton twitter 😊")],
      [fr("coucou")],
      [fr("hello toi"), fr("t'es vraiment trop belle")],
      [fr("salut ça va ?")],
      [fr("yo"), fr("c'est vraiment toi ?")],
    ],
    retours: [fr("coucou t'es là ?"), fr("tu fais quoi ce soir ?"), fr("je pensais à toi"), fr("re")],
    repliques: [fr("ah ouais ?"), fr("raconte"), fr("j'aime bien quand tu parles comme ça"), fr("t'es chaude toi 😏")],
    prix: [fr("c'est combien ?"), fr("tu vends quoi du coup ?")],
  },
  en: {
    langue: "en",
    prenoms: ["Connor", "Jake", "Liam", "Ethan", "Tyler", "Ryan"],
    arrivees: [
      [{ texte: "hey", fr: "salut" }, { texte: "saw you on twitter, you're gorgeous", fr: "je t'ai vue sur twitter, t'es magnifique" }],
      [{ texte: "hi beautiful", fr: "salut ma belle" }],
      [{ texte: "hello there 😊", fr: "salut toi 😊" }],
    ],
    retours: [
      { texte: "hey you there?", fr: "hé t'es là ?" },
      { texte: "missed you today", fr: "tu m'as manqué aujourd'hui" },
    ],
    repliques: [
      { texte: "oh really?", fr: "ah ouais ?" },
      { texte: "tell me more", fr: "dis m'en plus" },
      { texte: "you're so hot", fr: "t'es trop sexy" },
    ],
    prix: [
      { texte: "how much for a video?", fr: "c'est combien pour une vidéo ?" },
      { texte: "what do you sell?", fr: "tu vends quoi ?" },
    ],
  },
  de: {
    langue: "de",
    prenoms: ["Jonas", "Lukas", "Felix", "Niklas"],
    arrivees: [
      [{ texte: "hallo", fr: "salut" }, { texte: "bist du wirklich so süß wie auf den fotos?", fr: "t'es vraiment aussi mignonne que sur les photos ?" }],
      [{ texte: "hey na", fr: "hé salut" }],
    ],
    retours: [
      { texte: "bist du da?", fr: "t'es là ?" },
      { texte: "was machst du heute abend?", fr: "tu fais quoi ce soir ?" },
    ],
    repliques: [
      { texte: "echt?", fr: "sérieux ?" },
      { texte: "erzähl mir mehr", fr: "dis m'en plus" },
      { texte: "du machst mich verrückt", fr: "tu me rends fou" },
    ],
    prix: [
      { texte: "was kostet ein video?", fr: "combien coûte une vidéo ?" },
      { texte: "was verkaufst du?", fr: "tu vends quoi ?" },
    ],
  },
  it: {
    langue: "it",
    prenoms: ["Luca", "Andrea", "Matteo", "Marco"],
    arrivees: [
      [{ texte: "ciao", fr: "salut" }, { texte: "ti ho trovata su twitter", fr: "je t'ai trouvée sur twitter" }],
      [{ texte: "ciao bella", fr: "salut ma belle" }],
    ],
    retours: [
      { texte: "ci sei?", fr: "t'es là ?" },
      { texte: "mi sei mancata", fr: "tu m'as manqué" },
    ],
    repliques: [
      { texte: "davvero?", fr: "vraiment ?" },
      { texte: "raccontami", fr: "raconte-moi" },
      { texte: "sei bellissima", fr: "t'es magnifique" },
    ],
    prix: [{ texte: "quanto costa un video?", fr: "combien coûte une vidéo ?" }],
  },
  es: {
    langue: "es",
    prenoms: ["Javier", "Pablo", "Sergio", "Diego"],
    arrivees: [
      [{ texte: "hola", fr: "salut" }, { texte: "te vi en twitter 😍", fr: "je t'ai vue sur twitter 😍" }],
      [{ texte: "hola guapa", fr: "salut ma belle" }],
    ],
    retours: [
      { texte: "estás ahí?", fr: "t'es là ?" },
      { texte: "pensando en ti", fr: "je pense à toi" },
    ],
    repliques: [
      { texte: "ah sí?", fr: "ah ouais ?" },
      { texte: "cuéntame", fr: "raconte-moi" },
      { texte: "me encantas", fr: "tu me plais trop" },
    ],
    prix: [{ texte: "cuánto cuesta un vídeo?", fr: "combien coûte une vidéo ?" }],
  },
  pt: {
    langue: "pt",
    prenoms: ["Rafael", "Tiago", "Bruno"],
    arrivees: [
      [{ texte: "olá", fr: "salut" }, { texte: "vi-te no twitter", fr: "je t'ai vue sur twitter" }],
    ],
    retours: [{ texte: "estás aí?", fr: "t'es là ?" }],
    repliques: [
      { texte: "a sério?", fr: "sérieux ?" },
      { texte: "conta lá", fr: "raconte" },
    ],
    prix: [{ texte: "quanto custa um vídeo?", fr: "combien coûte une vidéo ?" }],
  },
};

/** Les mots qui disent qu'il veut ACHETER, toutes langues. */
export const MOTS_DU_PRIX =
  /combien|c'est quoi le prix|tarif|tu vends|how much|price|what do you sell|was kostet|wie viel|verkaufst|quanto cost|cuánto|cuanto|quanto custa/i;
/** … qu'il trouve ça cher. */
export const MOTS_DE_L_OBJECTION =
  /cher|moins cher|trop|expensive|cheaper|teuer|caro|barato/i;
/** … qu'il veut du réel — un appel, une cam, une rencontre, son snap. */
export const MOTS_DU_REEL =
  /en vrai|se voir|rencontr|appel|visio|cam\b|snap|numéro|meet|call|treffen|vederci|vernos/i;
/** … qu'il veut du gratuit. */
export const MOTS_DU_GRATUIT = /gratuit|gratos|pour voir|free|kostenlos|gratis/i;
